import React from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

type DocCitationType = "manual" | "procedure" | "inspection" | "sub-asset";
type RecordCitationType = "work-order" | "invoice" | "quotation" | "company";
type CitationType = DocCitationType | RecordCitationType;

interface Citation {
  id: string;
  title: string;
  subtitle?: string;
  type: CitationType;
}

interface MockResponse {
  content: string;
  citations: Citation[];
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations?: Citation[];
}

const DOC_TYPES = new Set<CitationType>(["manual", "procedure", "inspection", "sub-asset"]);

// ─── Keyword → Mock Response Map ─────────────────────────────────────────────

const KEYWORD_RESPONSES: { keywords: string[]; response: MockResponse }[] = [
  {
    keywords: ["work order", "workorder"],
    response: {
      content:
        "There are 3 active work orders linked to this asset. The most recent is WO-2026-0312 opened on 10 March 2026 for a hydraulic seal replacement — currently In Progress and assigned to the Plant A team.\n\nWO-2026-0289 was completed on 28 February 2026 following a routine overhaul. WO-2026-0241 is on hold pending spare parts delivery.",
      citations: [
        { id: "r1", title: "WO-2026-0312", subtitle: "Hydraulic Seal Replacement · In Progress", type: "work-order" },
        { id: "r2", title: "WO-2026-0289", subtitle: "Routine Overhaul · Completed", type: "work-order" },
        { id: "r3", title: "WO-2026-0241", subtitle: "Spare Parts Order · On Hold", type: "work-order" },
      ],
    },
  },
  {
    keywords: ["sub asset", "subasset", "sub-asset", "hierarchy", "child asset"],
    response: {
      content:
        "This asset has 3 registered sub-assets forming a two-level hierarchy:\n\n• Press Module Alpha — contains 2 further sub-assets (Cylinder Unit A and Cylinder Unit B)\n• Hydraulic Pump Unit — standalone\n• Control Panel v3 — standalone\n\nAll sub-assets are active and last inspected in Q1 2026.",
      citations: [
        { id: "r1", title: "Press Module Alpha", type: "sub-asset" },
        { id: "r2", title: "Hydraulic Pump Unit", type: "sub-asset" },
        { id: "r3", title: "Control Panel v3", type: "sub-asset" },
        { id: "r4", title: "Cylinder Unit A", type: "sub-asset" },
      ],
    },
  },
  {
    keywords: ["invoice"],
    response: {
      content:
        "Two invoices are linked to maintenance activities for this asset. IN-2026-0048 for €3,200 covers the hydraulic seal replacement parts and labour from March 2026 — currently marked as Paid. IN-2026-0031 for €840 covers filter consumables and is still Outstanding.",
      citations: [
        { id: "r1", title: "IN-2026-0048", subtitle: "€3,200 · Paid · March 2026", type: "invoice" },
        { id: "r2", title: "IN-2026-0031", subtitle: "€840 · Outstanding · Filter Consumables", type: "invoice" },
      ],
    },
  },
  {
    keywords: ["quotation", "quote", "quotations"],
    response: {
      content:
        "There are 2 quotations on record related to this asset. QT-2026-0019 for €5,400 covers a full hydraulic system overhaul — currently in Draft and awaiting client approval. QT-2026-0011 for €1,200 covered the last filter replacement service and has been converted to an invoice.",
      citations: [
        { id: "r1", title: "QT-2026-0019", subtitle: "€5,400 · Draft · Awaiting Approval", type: "quotation" },
        { id: "r2", title: "QT-2026-0011", subtitle: "€1,200 · Converted to Invoice", type: "quotation" },
      ],
    },
  },
  {
    keywords: ["company", "companies", "client", "customer"],
    response: {
      content:
        "This asset is registered under Acme International, headquartered in Berlin. The asset is located at their Plant A facility in Munich. The primary contact for maintenance requests is Thomas Brandt (Operations Manager).\n\nAcme International has 12 assets registered across 3 locations in your system.",
      citations: [
        { id: "r1", title: "Acme International", subtitle: "Berlin, Germany · 12 Assets", type: "company" },
        { id: "r2", title: "Plant A – Munich", subtitle: "Acme International · Primary Site", type: "company" },
      ],
    },
  },
  {
    keywords: ["document", "manual", "documentation", "docs"],
    response: {
      content:
        "4 documents are attached to this asset. The MK-3000 Service Manual (Rev. 3) is the primary technical reference, covering installation, operation, and maintenance procedures. Additional documents include the CE Declaration of Conformity, Electrical Wiring Diagram v2, and the Original Factory Test Report from 2021.",
      citations: [
        { id: "r1", title: "MK-3000 Service Manual Rev.3", type: "manual" },
        { id: "r2", title: "CE Declaration of Conformity", type: "manual" },
        { id: "r3", title: "Electrical Wiring Diagram v2", type: "manual" },
        { id: "r4", title: "Factory Test Report 2021", type: "manual" },
      ],
    },
  },
  {
    keywords: ["procedure", "pm", "preventive", "checklist"],
    response: {
      content:
        "3 PM procedures are assigned to this asset on a recurring schedule:\n\n• Monthly Visual Inspection — last completed 1 May 2026\n• Quarterly Hydraulic Service — next due 15 June 2026\n• Annual Full Overhaul — next due January 2027\n\nAll procedures are currently on track with no overdue items.",
      citations: [
        { id: "r1", title: "Monthly Visual Inspection", type: "procedure" },
        { id: "r2", title: "Quarterly Hydraulic Service", type: "procedure" },
        { id: "r3", title: "Annual Full Overhaul", type: "procedure" },
      ],
    },
  },
  {
    keywords: ["inspection", "report", "audit"],
    response: {
      content:
        "The last inspection was carried out on 15 March 2026 by the Plant A maintenance team. Overall condition was rated Good. Two minor observations were logged — a slight oil seep on the left cylinder seal and a worn mounting bolt on the pressure gauge — both addressed in WO-2026-0312.\n\nThe next scheduled inspection is due 15 June 2026.",
      citations: [
        { id: "r1", title: "Inspection Report – Mar 2026", type: "inspection" },
        { id: "r2", title: "WO-2026-0312", subtitle: "Hydraulic Seal Replacement · In Progress", type: "work-order" },
        { id: "r3", title: "MK-3000 Service Manual", type: "manual" },
      ],
    },
  },
  {
    keywords: ["asset", "machine", "equipment", "overview", "info", "details"],
    response: {
      content:
        "The Hydraulic Press MK-3000 (Serial: HP3K-001XP) is registered at Plant A, Acme International — Munich. It has been in service since January 2021 with a total of 4,820 operating hours logged.\n\nCurrent status: Active. Last maintenance: 15 March 2026. Next scheduled service: 15 June 2026. It has 3 sub-assets and 3 active work orders.",
      citations: [
        { id: "r1", title: "MK-3000 Service Manual", type: "manual" },
        { id: "r2", title: "WO-2026-0312", subtitle: "Hydraulic Seal Replacement · In Progress", type: "work-order" },
        { id: "r3", title: "Inspection Report – Mar 2026", type: "inspection" },
        { id: "r4", title: "Acme International", subtitle: "Berlin, Germany · 12 Assets", type: "company" },
      ],
    },
  },
];

const FALLBACK_RESPONSE: MockResponse = {
  content:
    "I can help you find information about this asset. Try asking about:\n\n• Asset details or overview\n• Work orders\n• Sub-assets\n• Documents or manuals\n• Procedures or PM schedules\n• Inspections\n• Invoices or quotations\n• Company information",
  citations: [],
};

function matchResponse(input: string): MockResponse {
  const lower = input.toLowerCase();
  for (const entry of KEYWORD_RESPONSES) {
    if (entry.keywords.some(kw => lower.includes(kw))) return entry.response;
  }
  return FALLBACK_RESPONSE;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#748094" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 8a5.5 5.5 0 1 0 1-3.2" />
      <path d="M2.5 3v2.5H5" />
      <path d="M8 5v3.5l2.5 1.5" />
    </svg>
  );
}

function SendIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M14 8L2 2l2.5 6L2 14l12-6z"
        fill={active ? "white" : "#a2aab8"}
        stroke={active ? "white" : "#a2aab8"}
        strokeWidth="0.5" strokeLinejoin="round"
      />
    </svg>
  );
}

function AiSparkleIcon({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
      <path d="M7 2 C7.15 5.4 8.6 6.85 12 7 C8.6 7.15 7.15 8.6 7 12 C6.85 8.6 5.4 7.15 2 7 C5.4 6.85 6.85 5.4 7 2Z" fill="#a2aab8" />
      <path d="M12.5 1 C12.6 2.7 13.3 3.4 15 3.5 C13.3 3.6 12.6 4.3 12.5 6 C12.4 4.3 11.7 3.6 10 3.5 C11.7 3.4 12.4 2.7 12.5 1Z" fill="#a2aab8" />
    </svg>
  );
}

// ─── Doc citation: pill tag ───────────────────────────────────────────────────

const DOC_ICON: Record<DocCitationType, React.ReactNode> = {
  manual: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#0517f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="1" width="8" height="11" rx="1" />
      <path d="M4 4.5h4M4 7h4M4 9.5h2.5" />
      <path d="M10 3.5h2v9h-2" />
    </svg>
  ),
  procedure: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#0517f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5" />
      <path d="M7 4v3.5l2 2" />
    </svg>
  ),
  inspection: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#0517f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="4" />
      <path d="M9.5 9.5L12.5 12.5" />
      <path d="M4.5 6l1.5 1.5 2.5-2.5" />
    </svg>
  ),
  "sub-asset": (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#0517f8" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4.5" y="1" width="5" height="4" rx="1" />
      <rect x="1" y="9" width="4" height="4" rx="1" />
      <rect x="9" y="9" width="4" height="4" rx="1" />
      <path d="M7 5v2M7 7H3v2M7 7h4v2" />
    </svg>
  ),
};

function SourcePill({ citation, index }: { citation: Citation; index: number }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "6px 8px", background: "#e6e8fe", borderRadius: 4,
      cursor: "pointer", flexShrink: 0,
    }}>
      <span style={{ fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px", color: "#0517f8", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
        {index + 1}.
      </span>
      <span style={{ fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px", color: "#0517f8", fontFamily: "var(--font-family-base)", whiteSpace: "nowrap" }}>
        {citation.title}
      </span>
      {DOC_ICON[citation.type as DocCitationType]}
    </div>
  );
}

// ─── Record citation: card ────────────────────────────────────────────────────

const RECORD_CONFIG: Record<RecordCitationType, { bg: string; icon: React.ReactNode }> = {
  "work-order": {
    bg: "#fff0e6",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#ff7a00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="14" height="13" rx="1.5" />
        <path d="M7 4V3a3 3 0 016 0v1" />
        <path d="M6.5 10h7M6.5 13h4.5" />
      </svg>
    ),
  },
  invoice: {
    bg: "#e6f9f1",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="2" width="14" height="16" rx="1.5" />
        <path d="M6.5 6.5h7M6.5 10h7M6.5 13.5h4" />
        <path d="M10 13.5v3" />
      </svg>
    ),
  },
  quotation: {
    bg: "#f3e8ff",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#9333ea" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2.5" y="3" width="15" height="12" rx="1.5" />
        <path d="M6 8h8M6 11h5" />
        <path d="M13.5 11l3 3" />
      </svg>
    ),
  },
  company: {
    bg: "#e6e8fe",
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#0517f8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2.5" y="6" width="15" height="12" rx="1.5" />
        <path d="M6.5 6V4a.5.5 0 01.5-.5h6a.5.5 0 01.5.5v2" />
        <path d="M2.5 11h15" />
        <path d="M8 11v7M12 11v7" />
      </svg>
    ),
  },
};

function RecordCard({ citation }: { citation: Citation }) {
  const cfg = RECORD_CONFIG[citation.type as RecordCitationType];
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 12px",
      background: "white",
      border: "1px solid #e8eaed",
      borderRadius: 8,
      cursor: "pointer",
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 8,
        background: cfg.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {cfg.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0,
          fontSize: 13, fontWeight: 700, lineHeight: "18px", letterSpacing: "-0.24px",
          color: "#172b4d", fontFamily: "var(--font-family-base)",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {citation.title}
        </p>
        {citation.subtitle && (
          <p style={{
            margin: "2px 0 0",
            fontSize: 12, fontWeight: 500, lineHeight: "16px",
            color: "#748094", fontFamily: "var(--font-family-base)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {citation.subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Message components ───────────────────────────────────────────────────────

function UserMessage({ content }: { content: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <div style={{ maxWidth: "85%", background: "#f3f4f6", borderRadius: 12, borderBottomRightRadius: 4, padding: "10px 14px" }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 500, lineHeight: "20px", color: "#172b4d", fontFamily: "var(--font-family-base)", whiteSpace: "pre-wrap" }}>
          {content}
        </p>
      </div>
    </div>
  );
}

function AssistantMessage({ content, citations }: { content: string; citations?: Citation[] }) {
  const docCitations = citations?.filter(c => DOC_TYPES.has(c.type)) ?? [];
  const recordCitations = citations?.filter(c => !DOC_TYPES.has(c.type)) ?? [];
  const hasSources = docCitations.length > 0 || recordCitations.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 500, lineHeight: "20px", color: "#172b4d", fontFamily: "var(--font-family-base)", whiteSpace: "pre-wrap" }}>
        {content}
      </p>

      {hasSources && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px", color: "#a2aab8", fontFamily: "var(--font-family-base)" }}>
            Relevant Sources
          </p>

          {/* Doc citations → pills */}
          {docCitations.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {docCitations.map((c, i) => (
                <SourcePill key={c.id} citation={c} index={i} />
              ))}
            </div>
          )}

          {/* Record citations → cards */}
          {recordCitations.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {recordCitations.map(c => (
                <RecordCard key={c.id} citation={c} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 24 }}>
      <AiSparkleIcon size={48} />
      <div style={{ textAlign: "center" }}>
        <p style={{ margin: 0, fontSize: 18, fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.36px", color: "#172b4d", fontFamily: "var(--font-family-base)" }}>
          Hello there!
        </p>
        <p style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 500, lineHeight: "20px", color: "#748094", fontFamily: "var(--font-family-base)" }}>
          How can I help you today?
        </p>
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

interface AskAIPanelProps {
  onClose: () => void;
}

export function AskAIPanel({ onClose }: AskAIPanelProps) {
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [input, setInput] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  function handleSend() {
    const text = input.trim();
    if (!text || isLoading) return;
    const userMsg: Message = { id: Date.now().toString(), role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsLoading(true);
    setTimeout(() => {
      const mock = matchResponse(text);
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: mock.content,
        citations: mock.citations.length > 0 ? mock.citations : undefined,
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsLoading(false);
    }, 1000);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  const hasMessages = messages.length > 0;
  const canSend = input.trim().length > 0 && !isLoading;

  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24,
      width: 480, height: "calc(100vh - 48px)", maxHeight: 680, minHeight: 320,
      background: "white", borderRadius: 12,
      boxShadow: "0 16px 32px 0 rgba(23,43,77,0.12), 0 0 2px 1px rgba(23,43,77,0.04)",
      display: "flex", flexDirection: "column", overflow: "hidden", zIndex: 500,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 16, borderBottom: "1px solid #e8eaed", flexShrink: 0 }}>
        <p style={{ flex: 1, margin: 0, fontSize: 18, fontWeight: 700, lineHeight: "28px", letterSpacing: "-0.36px", color: "#172b4d", fontFamily: "var(--font-family-base)" }}>
          Ask AI
        </p>
        <button
          onClick={() => setMessages([])}
          title="Clear conversation"
          style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "white", border: "1px solid #e8eaed", borderRadius: 8, cursor: "pointer", padding: 0, flexShrink: 0 }}
        >
          <HistoryIcon />
        </button>
        <button
          onClick={onClose}
          style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", background: "white", border: "1px solid #e8eaed", borderRadius: 8, cursor: "pointer", padding: 0, flexShrink: 0 }}
        >
          <CloseIcon />
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
        {!hasMessages ? (
          <EmptyState />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20, padding: 16 }}>
            {messages.map(msg =>
              msg.role === "user"
                ? <UserMessage key={msg.id} content={msg.content} />
                : <AssistantMessage key={msg.id} content={msg.content} citations={msg.citations} />
            )}
            {isLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#a2aab8", animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ borderTop: "1px solid #e8eaed", padding: "16px 16px 8px", display: "flex", flexDirection: "column", gap: 8, flexShrink: 0, background: "white" }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write your message here..."
          rows={2}
          style={{ width: "100%", border: "none", outline: "none", resize: "none", fontFamily: "var(--font-family-base)", fontSize: 14, fontWeight: 500, lineHeight: "20px", color: "#172b4d", background: "transparent", boxSizing: "border-box" }}
        />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 500, lineHeight: "16px", color: "#a2aab8", fontFamily: "var(--font-family-base)", whiteSpace: "pre-line", flex: 1 }}>
            AI Assistant can make mistakes.{"\n"}Consider checking important information.
          </p>
          <button
            onClick={handleSend}
            disabled={!canSend}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              height: 36, padding: "0 14px",
              background: canSend ? "#0517f8" : "#e8eaed",
              border: "none", borderRadius: 8,
              fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
              color: canSend ? "white" : "#a2aab8",
              cursor: canSend ? "pointer" : "not-allowed",
              whiteSpace: "nowrap", flexShrink: 0,
              transition: "background 120ms ease, color 120ms ease",
            }}
          >
            <SendIcon active={canSend} />
            Ask
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
